import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, downloadFile } from "@/lib/api";
import { Spinner } from "@/components/common";
import { SignaturePad } from "@/components/SignaturePad";
import { FilaLlamado } from "@/components/LlamadosAtencion";
import { AlertOctagon, PenLine, X, Eraser } from "lucide-react";

const ACEPTA = "Declaro que leí el acta completa, que la firma que dibujé es mía y autorizo su uso como firma electrónica solo para este documento (Ley 527 de 1999). Firmar significa que fui informado(a) del contenido.";

function FirmarLlamado({ x, onClose, onSigned }) {
  const pad = useRef(null);
  const [vacio, setVacio] = useState(true);
  const [ident, setIdent] = useState("");
  const [acepta, setAcepta] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const firmar = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/portal/llamados/${x.id}/firmar`, { trazo: pad.current.imagen(), identificacion: ident, acepta });
      toast.success("Firmaste el acta. Ya puedes descargarla.");
      onSigned();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 sm:items-center sm:p-4" data-testid="modal-firmar-llamado">
      <div className="max-h-[94vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-5 sm:rounded-2xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">Firmar llamado de atención</h3>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" data-testid="firmar-llamado-cerrar"><X className="h-5 w-5" /></button>
        </div>
        <ol className="mb-4 space-y-1 text-sm text-slate-600">
          <li>1. Lee el acta: <button onClick={() => downloadFile(`/llamados/${x.id}/documento`, "llamado_atencion.pdf")} className="font-semibold text-vino-700 underline" data-testid="firmar-llamado-leer">descargar acta</button></li>
          <li>2. Escribe tu número de documento y dibuja tu firma.</li>
        </ol>
        <input inputMode="numeric" value={ident} onChange={(e) => setIdent(e.target.value)} placeholder="Número de documento" className="mb-3 w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-base outline-none focus:border-vino-500" data-testid="firmar-llamado-ident" />
        <SignaturePad ref={pad} onChange={setVacio} />
        <button type="button" onClick={() => pad.current.limpiar()} className="mt-2 flex items-center gap-1 text-xs text-slate-500"><Eraser className="h-3.5 w-3.5" /> Borrar firma</button>
        <label className="mt-3 flex gap-2 text-xs text-slate-600"><input type="checkbox" checked={acepta} onChange={(e) => setAcepta(e.target.checked)} className="mt-0.5 h-5 w-5 accent-vino-700" data-testid="firmar-llamado-acepta" /> {ACEPTA}</label>
        {error && <p className="mt-3 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-testid="firmar-llamado-error">{error}</p>}
        <button onClick={firmar} disabled={busy || vacio || !acepta || !ident.trim()} data-testid="firmar-llamado-enviar"
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-vino-700 px-4 py-3.5 text-base font-semibold text-white hover:bg-vino-800 disabled:opacity-60">{busy ? <Spinner className="h-5 w-5" /> : <PenLine className="h-5 w-5" />} Firmar acta</button>
      </div>
    </div>
  );
}

export default function PortalLlamados() {
  const [items, setItems] = useState(null);
  const [firmando, setFirmando] = useState(null);
  const cargar = () => api.get("/portal/llamados").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { cargar(); }, []);
  return (
    <div className="space-y-5" data-testid="portal-llamados">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Llamados de atención</h1>
        <p className="mt-1 text-sm text-slate-500">Actas que HUMAN te ha expedido. Si alguna está pendiente, léela y fírmala aquí.</p>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        {!items ? <Spinner /> : items.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-slate-500" data-testid="portal-llamados-vacio"><AlertOctagon className="h-4 w-4" /> No tienes llamados de atención.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((x) => (
              <FilaLlamado key={x.id} x={x} acciones={x.faltan?.includes("empleado") && (
                <button onClick={() => setFirmando(x)} data-testid={`portal-firmar-llamado-${x.id}`} className="flex items-center gap-1 rounded-lg bg-vino-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-vino-800"><PenLine className="h-3.5 w-3.5" /> Firmar</button>
              )} />
            ))}
          </div>
        )}
      </div>
      {firmando && <FirmarLlamado x={firmando} onClose={() => setFirmando(null)} onSigned={() => { setFirmando(null); cargar(); }} />}
    </div>
  );
}
